import { useState } from "react";
import { ImageWithFallback } from "./ImageWithFallback";
import { ArrowRight, CalendarDays, ExternalLink } from "lucide-react";
import { ContactBookingModal } from "./ContactBookingModal";
import { ExpandableText } from "../ui/ExpandableText";
import { useLanguage } from "../../i18n/LanguageContext";
import { useSessionStorageState } from "../../hooks/useSessionStorageState";
import { useNavigate } from "react-router-dom";
import { DoctorDto } from "../../web-api-client";

interface DoctorCardProps {
  id: string;
  name: string;
  title: string;
  link: string;
  experience: string;
  avatar: string;
  position: string;
  isOpen?: boolean;
  hasSchedule?: boolean;
  onToggle?: (id: string | number) => void;
  doctorForSchedule?: DoctorDto;
}

export function CommanderCard({
  id,
  name,
  title,
  link,
  experience,
  avatar,
  position,
  hasSchedule,
  doctorForSchedule,
}: DoctorCardProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [, setSelectedDoctor] = useSessionStorageState("selectedDoctor", null);

  const [shape, setShape] = useState<
    "unknown" | "landscape" | "portrait" | "square"
  >("unknown");
  const [open, setOpen] = useState(false);

  const squareThreshold = 0.1;
  const isSquareish = shape === "square" || shape === "portrait";

  const handleRequestAppointment = (e: any) => {
    if (!hasSchedule) {
      e.preventDefault();
      e.stopPropagation();
      setOpen(true);
      return;
    }

    sessionStorage.clear();
    setSelectedDoctor(doctorForSchedule);

    const params = new URLSearchParams({
      type: "doctor",
      doctorId: id,
      step: "chooseTime",
    });

    setTimeout(() => {
      navigate(`/dat-hen?${params.toString()}`);
    }, 0);
  };

  return (
    <>
      <article
        className="
          group flex h-full flex-col overflow-hidden rounded-2xl bg-white
          border border-[#e6efe8]
          shadow-[0_8px_30px_rgba(16,24,40,0.06)]
          transition-all duration-300
          hover:-translate-y-1 hover:shadow-[0_16px_40px_rgba(16,24,40,0.10)]
        "
      >
        <figure className="relative aspect-[4/4] md:aspect-[3/4] overflow-hidden bg-white">
          <ImageWithFallback
            alt={name}
            loading="lazy"
            width="400"
            height="500"
            decoding="async"
            className={`h-full w-full transition-transform duration-500 group-hover:scale-[1.03] ${
              !isSquareish ? "object-cover" : "object-contain md:p-6 p-4"
            }`}
            src={avatar}
            onLoad={(e) => {
              const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
              if (!w || !h) return;

              const r = w / h;
              const nearSquare = Math.abs(r - 1) <= squareThreshold;

              if (nearSquare) setShape("square");
              else if (r > 1) setShape("landscape");
              else setShape("portrait");
            }}
          />

          <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/18 via-black/6 to-transparent" />

          {hasSchedule && (
            <div
              className="
                absolute left-4 bottom-4 z-10
                inline-flex items-center gap-1.5
                rounded-full border border-[#d7eadc]
                bg-[#f6fbf7]/95 px-3 py-1.5
                text-[11px] font-semibold text-[#166534]
                shadow-[0_4px_14px_rgba(15,23,42,0.08)]
                backdrop-blur-sm
              "
            >
              <span className="h-1.5 w-1.5 rounded-full bg-[#22c55e]" />
              <span>{t("colichtuanay")}</span>
            </div>
          )}
        </figure>

        <div className="flex flex-1 flex-col px-5 pt-4 pb-5">
          <div className="min-h-[104px]">
            <p className="text-sm font-medium text-[#4d6b57] line-clamp-1">
              {title}
            </p>

            <p className="mt-1 md:text-[1.7rem] text-xl leading-tight font-bold text-[#157347] line-clamp-2">
              {name}
            </p>

            <p className="mt-2 text-sm font-semibold text-[#2b3b30] line-clamp-2">
              {position}
            </p>
          </div>

          <div className="min-h-[72px] md:min-h-[72px]">
            <ExpandableText text={experience} lines={2} />
          </div>

          <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
            <a
              href={link}
              target="_blank"
              rel="noopener noreferrer"
              className="
                inline-flex items-center justify-center gap-2 rounded-xl
                border border-[#d8e7dc] bg-white px-4 py-3
                text-sm font-semibold text-[#215c39]
                transition hover:bg-[#f7fbf8] hover:border-[#bcd6c4]
              "
            >
              <span className="text-center">{t("thongtinchitiet")}</span>
              <ExternalLink className="h-4 w-4 md:hidden" />
            </a>

            <button
              type="button"
              onClick={(e) => handleRequestAppointment(e)}
              className="
                group/btn inline-flex items-center justify-center gap-2 rounded-xl
                bg-[linear-gradient(135deg,#2f7d44_0%,#368f4e_52%,#7cc46a_100%)]
                px-4 py-3 text-sm font-semibold text-white
                shadow-[0_10px_24px_rgba(9,48,24,0.22)]
                transition hover:-translate-y-[1px] hover:shadow-[0_16px_32px_rgba(9,48,24,0.28)]
              "
            >
              <CalendarDays className="h-4 w-4 md:hidden" />
              <span>
                {hasSchedule ? t("datlichkhamngay") : t("lienhedatkham")}
              </span>
              <ArrowRight className="h-4 w-4 md:hidden transition-transform group-hover/btn:translate-x-1" />
            </button>
          </div>
        </div>
      </article>

      <ContactBookingModal
        open={open}
        onClose={() => setOpen(false)}
        doctorId={id}
        doctorName={name}
      />
    </>
  );
}